import React, { useState, useMemo, useCallback, useEffect, Suspense, startTransition } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  LinearProgress,
} from "@mui/material";
import { useAuthContext } from "../../../context/AuthProvider";
import { useNavigate } from "react-router-dom";
import { useManifiestos, useManifestsPaged } from "../../../hooks/useManifest";
import { useServiceOrders } from "../../../hooks/administration/useServiceOrders";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  History as HistoryIcon,
  Add as AddIcon,
  Public as PublicIcon,
  WhatsApp as WhatsAppIcon,
  TravelExplore as TravelExploreIcon,
  PlaylistAdd as PlaylistAddIcon,
} from "@mui/icons-material";
import ManifiestoForm from "../../administracion/ManifestForm";
import ManifestOrderForm from "../../operations/manifest/ManifestOrderForm";
import BulkManifestFromOrdersDialog from "../../operations/manifest/BulkManifestFromOrdersDialog";

// Lazy-load del mapa (pesa bastante)
const RouteMap = React.lazy(() => import("../../administracion/rutas/RouteMap"));

// Colores estado
const ESTADO_COLOR_MAP = {
  CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff" },
  PLANIFICADO: { label: "PLANIFICADO", color: "#ffb300", text: "#000" },
  EN_PROCESO: { label: "EN PROCESO", color: "#4caf50", text: "#fff" },
  COMPLETADO: { label: "COMPLETADO", color: "#1e88e5", text: "#fff" },
  CANCELADO: { label: "CANCELADO", color: "#e53935", text: "#fff" },
};

/*const TAB_CONFIG = [
  { label: "Creados", filter: (m) => m.manest === "CREADO" },
  { label: "Planificados", filter: (m) => m.manest === "PLANIFICADO" },
  { label: "En Proceso", filter: (m) => m.manest === "EN_PROCESO" },
  { label: "Otros", filter: (m) => ["COMPLETADO", "CANCELADO"].includes(m.manest) },
];*/

const TAB_CONFIG = [
  {
    label: "Creados",
    estados: ["CREADO"],
  },
  {
    label: "Planificados",
    estados: ["PLANIFICADO"],
  },
  {
    label: "En Proceso",
    estados: ["EN_PROCESO"],
  },
  {
    label: "Otros",
    estados: ["COMPLETADO", "CANCELADO"],
  },
];

// UI: 50 por defecto suele ser mucho más fluido que 100 con MUI + tooltips + acciones.
const DEFAULT_LIMIT_UI = 50;

// Debounce simple
const useDebouncedValue = (value, delay = 300) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// Fila memoizada para reducir re-render masivo
const ManifestRow = React.memo(function ManifestRow({
  manifiesto,
  user,
  administradorID,
  operacionesID,
  adminID,
  formatFechaLarga,
  onEdit,
  onView,
  onMap,
  onStream,
  onHistory,
  onWhatsapp,
  onDelete,
}) {
  return (
    <TableRow hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
      <TableCell>
        <Typography fontWeight="bold" color="primary">
          MN-{String(manifiesto.manid).padStart(6, "0")}
        </Typography>
      </TableCell>

      <TableCell>
        {manifiesto.ordsercabid ? (
          <Typography fontWeight="bold">
            OS-{String(manifiesto.ordsercabid).padStart(6, "0")}
          </Typography>
        ) : (
          <Typography color="text.secondary">—</Typography>
        )}
      </TableCell>

      <TableCell sx={{ maxWidth: 300 }}>
        <Typography noWrap title={manifiesto.maninf}>
          {manifiesto.maninf}
        </Typography>
      </TableCell>

      <TableCell>{formatFechaLarga(manifiesto.manfec)}</TableCell>

      <TableCell sx={{ maxWidth: 200 }}>
        <Typography noWrap title={manifiesto.manori}>
          {manifiesto.manori}
        </Typography>
      </TableCell>

      <TableCell sx={{ maxWidth: 200 }}>
        <Typography noWrap title={manifiesto.mandes}>
          {manifiesto.mandes}
        </Typography>
      </TableCell>

      <TableCell>
        <Chip
          label={ESTADO_COLOR_MAP[manifiesto.manest]?.label || manifiesto.manest}
          sx={{
            backgroundColor: ESTADO_COLOR_MAP[manifiesto.manest]?.color || "#ccc",
            color: ESTADO_COLOR_MAP[manifiesto.manest]?.text || "#000",
            fontWeight: "bold",
            minWidth: 100,
          }}
          size="small"
        />
      </TableCell>

      <TableCell align="center" sx={{ minWidth: 180 }}>
        <Box display="flex" justifyContent="center" gap={1}>
          {(user?.RolID === administradorID || user?.RolID === operacionesID) && (
            <Tooltip title="Editar">
              <IconButton onClick={() => onEdit(manifiesto)} size="small">
                <EditIcon color="primary" fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Ver detalles">
            <IconButton onClick={() => onView(manifiesto.manid)} size="small">
              <VisibilityIcon sx={{ color: "#009688" }} fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Ver mapa de ruta">
            <IconButton onClick={() => onMap(manifiesto)} size="small">
              <PublicIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>

          {[adminID, administradorID, operacionesID].includes(user?.RolID) && (
            <Tooltip title="Monitor de Viaje">
              <IconButton onClick={() => onStream(manifiesto)} size="small">
                <TravelExploreIcon sx={{ color: "#009688" }} fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {user?.RolID === adminID && (
            <>
              <Tooltip title="Ver historial">
                <IconButton onClick={() => onHistory(manifiesto.manid)} size="small">
                  <HistoryIcon color="secondary" fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title={manifiesto.whatsappsent ? "Mensaje ya enviado" : "Enviar WhatsApp"}>
                <span>
                  <IconButton
                    onClick={() => onWhatsapp(manifiesto)}
                    disabled={manifiesto.whatsappsent}
                    color={manifiesto.whatsappsent ? "default" : "success"}
                    size="small"
                  >
                    <WhatsAppIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Eliminar">
                <IconButton onClick={() => onDelete(manifiesto)} size="small">
                  <DeleteIcon color="error" fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
});

const ManifestDashboardBase = ({
  tipo,
  tipoServicioCodigo,
  tipoServicioId,
  serProInt,
  title,
  allowBulkCreation = false,
  allowCreation = true,
}) => {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const resolvedSerProInt = useMemo(() => {
    if (typeof serProInt === "boolean") {
      return serProInt;
    }

    if (tipo === "internos") {
      return true;
    }

    if (tipo === "externos") {
      return false;
    }

    return undefined;
  }, [serProInt, tipo]);

  // Modales / estados varios
  const [openForm, setOpenForm] = useState(false);
  const [openFormV2, setOpenFormV2] = useState(false);
  const [editManifiesto, setEditManifiesto] = useState(null);

  const [openConfirm, setOpenConfirm] = useState(false);
  const [openConfirmWhatsapp, setOpenConfirmWhatsapp] = useState(false);

  const [openWhatsappResult, setOpenWhatsappResult] = useState(false);
  const [whatsappResult, setWhatsappResult] = useState(null);

  const [selectedManifiesto, setSelectedManifiesto] = useState(null);
  const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });

  const [openMap, setOpenMap] = useState(false);
  const [mapData, setMapData] = useState({ id: "", vehicleId: null });

  const [openBulkManifest, setOpenBulkManifest] = useState(false);

  // Paginación server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_LIMIT_UI);

  // Filtros client-side (sobre la página actual)
  const [search, setSearch] = useState("");
  const [codigoFilter, setCodigoFilter] = useState("");
  const [currentTab, setCurrentTab] = useState(0);

  const searchDebounced = useDebouncedValue(search, 300);
  const codigoDebounced = useDebouncedValue(codigoFilter, 300);

  const { basicServiceOrders4Form } = useServiceOrders();

  // Mutaciones/acciones existentes
  const { deleteMutation, sendMessageWhatsappMutation } = useManifiestos();

  // Cargar lista paginada (internos/externos)
  const offset = page * rowsPerPage;

  /*const { data: paged, isLoading, error, isFetching } = useManifestsPaged({
    serProInt: resolvedSerProInt,
    tipoServicioId,
    tipoServicioCodigo,
    limit: rowsPerPage,
    offset,
    enabled: true,
  });*/

  const selectedEstados = TAB_CONFIG[currentTab].estados;

  const estados =
    selectedEstados.length === 1
      ? selectedEstados[0]
      : undefined;

  const {
    data: paged,
    isLoading,
    isFetching,
    error,
  } = useManifestsPaged({
    serProInt: resolvedSerProInt,
    tipoServicioId,
    tipoServicioCodigo,
    estados,
    limit: rowsPerPage,
    offset,
    enabled: true,
  });

  const manifiestos = paged?.data ?? [];

  // Roles
  const administradorID = Number(process.env.REACT_APP_ROL_ADMINISTRADOR_ID);
  const operacionesID = Number(process.env.REACT_APP_ROL_OPERACIONES_ID);
  const adminID = Number(process.env.REACT_APP_ROL_ADMIN_ID);

  const showAlert = useCallback((message, severity = "success") => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert((prev) => ({ ...prev, open: false })), 5000);
  }, []);

  const formatFechaLarga = useCallback((dateString) => {
    if (!dateString) return '';
    const fecha = new Date(dateString + 'T00:00:00');
    return new Intl.DateTimeFormat('es-PE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    }).format(fecha);
  }, []);

  // Filtrado en front SOLO sobre la página recibida (ya viene ordenada desde backend)
  /*const displayedManifiestos = useMemo(() => {
    const s = (searchDebounced || "").toLowerCase().trim();
    const cf = (codigoDebounced || "").toLowerCase().trim();

    return (manifiestos || []).filter((manifiesto) => {
      const manidStr = String(manifiesto.manid ?? "");
      const codigoCompleto = `mn-${manidStr.padStart(6, "0")}`;

      const ori = (manifiesto.manori || "").toLowerCase();
      const des = (manifiesto.mandes || "").toLowerCase();

      const matchesSearch = !s || ori.includes(s) || des.includes(s);

      const matchesCodigo =
        !cf ||
        codigoCompleto.includes(cf) ||
        manidStr.includes(cf.replace(/^mn-0+/i, ""));

      return matchesSearch && matchesCodigo && TAB_CONFIG[currentTab].filter(manifiesto);
    });
  }, [manifiestos, searchDebounced, codigoDebounced, currentTab]);*/

  const displayedManifiestos = useMemo(() => {
    const s = (searchDebounced || "").toLowerCase().trim();
    const cf = (codigoDebounced || "").toLowerCase().trim();

    return manifiestos.filter((manifiesto) => {
      const manidStr = String(manifiesto.manid ?? "");
      const codigoCompleto = `mn-${manidStr.padStart(6, "0")}`;

      const ori = (manifiesto.manori || "").toLowerCase();
      const des = (manifiesto.mandes || "").toLowerCase();

      const matchesSearch =
        !s ||
        ori.includes(s) ||
        des.includes(s);

      const matchesCodigo =
        !cf ||
        codigoCompleto.includes(cf) ||
        manidStr.includes(cf.replace(/^mn-0+/i, ""));

      const matchesEstado =
        TAB_CONFIG[currentTab].estados.includes(manifiesto.manest);

      return matchesSearch && matchesCodigo && matchesEstado;
    });
  }, [
    manifiestos,
    searchDebounced,
    codigoDebounced,
    currentTab,
  ]);

  // Handlers memoizados (para ayudar a React.memo en filas)
  const handleTabChange = useCallback((_, newValue) => {
    startTransition(() => {
      setCurrentTab(newValue);
      setPage(0);
    });
  }, []);

  const handleChangePage = useCallback((_, newPage) => {
    startTransition(() => setPage(newPage));
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    const next = parseInt(event.target.value, 10);
    startTransition(() => {
      setRowsPerPage(next);
      setPage(0);
    });
  }, []);

  const onEdit = useCallback((man) => {
    setEditManifiesto(man);
    setOpenFormV2(true);
  }, []);

  const onView = useCallback((id) => {
    navigate(`/administracion/manifiestos/${id}/pasajeros`);
  }, [navigate]);

  const onMap = useCallback((man) => {
    setMapData({ id: man.manid, vehicleId: man.vehid });
    setOpenMap(true);
  }, []);

  const onStream = useCallback((man) => {
    navigate(`/administracion/manifiestos/${man.manid}/stream`, { state: { vehid: man.vehid } });
  }, [navigate]);

  const onHistory = useCallback((id) => {
    navigate(`/administracion/manifiestos/${id}/historial`);
  }, [navigate]);

  const onWhatsapp = useCallback((man) => {
    setSelectedManifiesto(man);
    setOpenConfirmWhatsapp(true);
  }, []);

  const onDelete = useCallback((man) => {
    setSelectedManifiesto(man);
    setOpenConfirm(true);
  }, []);

  const handleDelete = useCallback(() => {
    if (!selectedManifiesto) return;

    deleteMutation.mutate(selectedManifiesto.manid, {
      onSuccess: () => {
        showAlert("Manifiesto eliminado con éxito");
        setOpenConfirm(false);
        setSelectedManifiesto(null);
      },
      onError: (err) => {
        showAlert(err?.message || "Error al eliminar el manifiesto", "error");
        setOpenConfirm(false);
      },
    });
  }, [deleteMutation, selectedManifiesto, showAlert]);

  /*const handleSubmitWhatsapp = useCallback(() => {
    if (!selectedManifiesto) return;

    sendMessageWhatsapp(selectedManifiesto.manid, {
      onSuccess: (result) => {
        setWhatsappResult(result);
        setOpenWhatsappResult(true);
      },
      onError: (err) => {
        showAlert(err?.message || "Error al enviar los mensajes", "error");
      },
      onSettled: () => {
        setOpenConfirmWhatsapp(false);
        setSelectedManifiesto(null);
      },
    });
  }, [selectedManifiesto, sendMessageWhatsapp, showAlert]);*/

  const handleSubmitWhatsapp = useCallback(() => {
    if (!selectedManifiesto) return;

    sendMessageWhatsappMutation.mutate(selectedManifiesto.manid, {
      onSuccess: (result) => {
        setWhatsappResult(result);
        setOpenWhatsappResult(true);
      },
      onError: (err) => {
        showAlert(
          err?.response?.data?.error ||
          err?.message ||
          "Error al enviar los mensajes",
          "error"
        );
      },
      onSettled: () => {
        setOpenConfirmWhatsapp(false);
        setSelectedManifiesto(null);
      },
    });
  }, [
    selectedManifiesto,
    sendMessageWhatsappMutation,
    showAlert,
  ]);



  const handleCloseResultModal = useCallback(() => {
    setOpenWhatsappResult(false);
    setWhatsappResult(null);
  }, []);

  // Determinar si hay siguiente página (cuando la página vino incompleta)
  const pageCount = paged?.pagination?.count ?? manifiestos.length;
  const disableNext = pageCount < rowsPerPage;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ my: 2 }}>
        Error al cargar manifiestos: {error.message}
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2, position: "relative" }}>
      {isFetching && !isLoading && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
          }}
        />
      )}

      {/*
      {tipo === "internos" && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            {title || `Gestión de Manifiestos Internos`}
          </Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              onClick={() => setOpenBulkManifest(true)}
              sx={{ textTransform: "none" }}
            >
              Crear masivo
            </Button>

            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditManifiesto(null);
                setOpenFormV2(true);
              }}
              sx={{ textTransform: "none" }}
            >
              Nuevo Manifiesto
            </Button>
          </Box>
        </Box>
      )}
      */}

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        gap={2}
      >
        <Typography variant="h5" fontWeight="bold">
          {title || "Gestión de Manifiestos"}
        </Typography>

        <Box display="flex" gap={1}>
          {allowBulkCreation && (
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              onClick={() => setOpenBulkManifest(true)}
              sx={{ textTransform: "none" }}
            >
              Crear masivo
            </Button>
          )}

          {allowCreation && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditManifiesto(null);
                setOpenFormV2(true);
              }}
              sx={{ textTransform: "none" }}
            >
              Nuevo Manifiesto
            </Button>
          )}
        </Box>
      </Box>

      {/*
      {tipo !== "internos" && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            {title || `Gestión de Manifiestos (${tipo === "internos" ? "Internos" : "Externos"})`}
          </Typography>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditManifiesto(null);
              setOpenFormV2(true);
            }}
            sx={{ textTransform: "none" }}
          >
            Nuevo Manifiesto
          </Button>
        </Box>
      )}
      */}

      {/* Filtros (sobre la página actual con debounce) */}
      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <TextField
          label="Buscar por origen/destino (página actual)"
          variant="outlined"
          fullWidth
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flex: 2, minWidth: 250 }}
        />
        <TextField
          label="Buscar por código (página actual)"
          variant="outlined"
          fullWidth
          size="small"
          value={codigoFilter}
          onChange={(e) => setCodigoFilter(e.target.value)}
          sx={{ flex: 1, minWidth: 250 }}
        />
      </Box>

      {/* Tabs estado */}
      <Tabs
        value={currentTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{
          mb: 3,
          borderRadius: 1,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
          "& .MuiTabs-indicator": { display: "none" },
          "& .MuiTab-root": {
            minHeight: 48,
            fontWeight: 600,
            fontSize: "0.9rem",
            color: "text.secondary",
            "&.Mui-selected": { color: "#000" },
          },
        }}
      >
        {TAB_CONFIG.map((tab, index) => (
          <Tab key={index} label={tab.label} />
        ))}
      </Tabs>

      {/* Tabla */}
      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e0e0e0" }}>
        <Table>
          <TableHead sx={{ bgcolor: "background.default" }}>
            <TableRow>
              <TableCell><strong>Código</strong></TableCell>
              <TableCell><strong>Orden</strong></TableCell>
              <TableCell><strong>Descripción</strong></TableCell>
              <TableCell><strong>Fecha</strong></TableCell>
              <TableCell><strong>Origen</strong></TableCell>
              <TableCell><strong>Destino</strong></TableCell>
              <TableCell><strong>Estado</strong></TableCell>
              <TableCell align="center"><strong>Acciones</strong></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {displayedManifiestos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <Typography variant="subtitle1" color="text.secondary">
                    No se encontraron manifiestos en esta página con esos filtros
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              displayedManifiestos.map((m) => (
                <ManifestRow
                  key={m.manid}
                  manifiesto={m}
                  user={user}
                  administradorID={administradorID}
                  operacionesID={operacionesID}
                  adminID={adminID}
                  formatFechaLarga={formatFechaLarga}
                  onEdit={onEdit}
                  onView={onView}
                  onMap={onMap}
                  onStream={onStream}
                  onHistory={onHistory}
                  onWhatsapp={onWhatsapp}
                  onDelete={onDelete}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación server-side */}
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={-1} // sin total real no se puede calcular páginas; usa next/prev
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        nextIconButtonProps={{ disabled: disableNext }}
        sx={{ mt: 2 }}
      />

      {/* Modales (montados siempre, pero son relativamente ligeros) */}
      <ManifiestoForm
        open={openForm}
        handleClose={() => setOpenForm(false)}
        defaultValues={editManifiesto}
      />

      <ManifestOrderForm
        open={openFormV2}
        handleClose={() => setOpenFormV2(false)}
        defaultValues={editManifiesto}
        orders={basicServiceOrders4Form}
      />

      {/* Confirm delete */}
      {openConfirm && (
        <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
          <DialogTitle>Confirmar eliminación</DialogTitle>
          <DialogContent>
            <DialogContentText>
              ¿Está seguro de eliminar el manifiesto{" "}
              {selectedManifiesto && `MN-${String(selectedManifiesto.manid).padStart(6, "0")}`}?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirm(false)}>Cancelar</Button>
            <Button
              onClick={handleDelete}
              variant="contained"
              color="error"
              disabled={deleteMutation.isLoading}
            >
              {deleteMutation.isLoading ? <CircularProgress size={24} /> : "Eliminar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Confirm whatsapp */}
      {openConfirmWhatsapp && (
        <Dialog open={openConfirmWhatsapp} onClose={() => setOpenConfirmWhatsapp(false)}>
          <DialogTitle>Enviar notificación por WhatsApp</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Se enviará un mensaje a todos los pasajeros del manifiesto{" "}
              {selectedManifiesto && `MN-${String(selectedManifiesto.manid).padStart(6, "0")}`}. ¿Desea continuar?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenConfirmWhatsapp(false)}>Cancelar</Button>
            {/*<Button
              onClick={handleSubmitWhatsapp}
              variant="contained"
              color="success"
              disabled={sendMessageWhatsapp.isLoading}
              startIcon={sendMessageWhatsapp.isLoading ? <CircularProgress size={20} /> : null}
            >
              {sendMessageWhatsapp.isLoading ? "Enviando..." : "Confirmar"}
            </Button>*/}
            <Button
              onClick={handleSubmitWhatsapp}
              variant="contained"
              color="success"
              disabled={sendMessageWhatsappMutation.isPending}
              startIcon={
                sendMessageWhatsappMutation.isPending
                  ? <CircularProgress size={20} />
                  : null
              }
            >
              {sendMessageWhatsappMutation.isPending
                ? "Enviando..."
                : "Confirmar"}
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Result whatsapp (montaje condicional) */}
      {openWhatsappResult && (
        <Dialog open={openWhatsappResult} onClose={handleCloseResultModal} fullWidth maxWidth="sm">
          <DialogTitle>Resultado del envío</DialogTitle>
          <DialogContent dividers>
            {whatsappResult && (
              <Box>
                <Box display="flex" justifyContent="space-between" mb={2}>
                  <Typography><strong>Enviados:</strong> {whatsappResult.enviados}</Typography>
                  <Typography color="error"><strong>Fallidos:</strong> {whatsappResult.omitidos}</Typography>
                </Box>

                {whatsappResult.omitidos > 0 && (
                  <>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle1" color="error" gutterBottom>
                      Detalle de errores:
                    </Typography>
                    <Box maxHeight={300} overflow="auto">
                      <List dense>
                        {whatsappResult.pasajerosFail.map((fail, index) => (
                          <ListItem key={index} disablePadding>
                            <ListItemText
                              primary={`${index + 1}. ${fail.name || "Sin nombre"} (${fail.number || "Sin número"})`}
                              secondary={fail.reason?.error?.error_data?.details || "Error desconocido"}
                              secondaryTypographyProps={{ color: "error" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseResultModal} variant="contained">Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Mapa (montaje condicional + lazy) */}
      {openMap && (
        <Dialog
          open={openMap}
          onClose={() => setOpenMap(false)}
          fullWidth
          maxWidth="lg"
          sx={{ "& .MuiDialog-paper": { height: "80vh" } }}
        >
          <DialogTitle>Mapa de ruta - MN-{String(mapData.id).padStart(6, "0")}</DialogTitle>
          <DialogContent dividers>
            <Suspense
              fallback={
                <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
                  <CircularProgress />
                </Box>
              }
            >
              <RouteMap route={mapData.id} vehid={mapData.vehicleId} />
            </Suspense>
          </DialogContent>
        </Dialog>
      )}

      {/*
      <BulkManifestFromOrdersDialog
        open={openBulkManifest}
        handleClose={() => setOpenBulkManifest(false)}
        user={user}
        tipo={tipo}
        onSuccess={() => {
          showAlert("Manifiestos creados correctamente.");
          setOpenBulkManifest(false);
        }}
      />*/}

      {allowBulkCreation && (
        <BulkManifestFromOrdersDialog
          open={openBulkManifest}
          handleClose={() => setOpenBulkManifest(false)}
          user={user}
          tipo={tipo}
          onSuccess={() => {
            showAlert("Manifiestos creados correctamente.");
            setOpenBulkManifest(false);
          }}
        />
      )}
    </Paper>
  );
};

export default ManifestDashboardBase;